import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { authHelpers, db } from '../../firebaseClient';
import { doc, getDoc } from 'firebase/firestore';
import { getUserOrgId, getOrganization } from '../../utils/orgHelpers';

const NavigationBar = ({ themed = false, onDark = false }) => {
  // Public site (themed=false, the default) gets these as hardcoded literal
  // values via inline style — never CSS variables, never Tailwind's bg-card
  // class. This guarantees the public nav can never be affected by the
  // dashboard's "dark" class or its --color-* variables, no matter what.
  // Only the dashboard passes themed to opt into the real light/dark values.
  const BASE_LIGHT = {
    pill: { backgroundColor: 'rgba(255,255,255,0.95)', borderColor: '#e5e7eb', color: '#1f2937' },
    pillTransparent: { backgroundColor: 'transparent', borderColor: 'transparent', color: '#1f2937' },
    wordmark: { color: '#111827' },
    exploreBtn: { color: '#1f2937' },
    searchWrap: { backgroundColor: '#f9fafb', borderColor: '#e5e7eb' },
    searchIcon: { color: '#6b7280' },
    drawer: { backgroundColor: '#ffffff', color: '#1f2937' },
  };
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [orgProfile, setOrgProfile] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVideoMode, setIsVideoMode] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(true);
    const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // White-on-dark mode for pages with a dark hero (e.g. For Individuals).
  // Only applies while the pill is transparent; once scrolled, the pill is
  // white again so we fall back to the normal dark styling.
  const light = onDark && !themed && !isScrolled;
  const FIXED_LIGHT = light
    ? {
        ...BASE_LIGHT,
        wordmark: { color: '#ffffff' },
        exploreBtn: { color: '#ffffff' },
      }
    : BASE_LIGHT;

  // Explore mega-menu — course categories + audience pages + platform links,
  // grouped into columns the way premium edtech platforms structure it
    const exploreSections = [
    {
      heading: 'Browse Courses',
      items: [
        { label: 'Traditional Trades', path: '/course-catalog?category=Traditional%20Trades' },
        { label: 'Emerging Tech', path: '/course-catalog?category=Emerging%20Tech' },
        { label: 'AI & Blockchain', path: '/course-catalog?category=AI%20%26%20Blockchain' },
        { label: 'Human Skills', path: '/course-catalog?category=Human%20Skills' },
        { label: 'All Courses', path: '/course-catalog' },
      ],
    },
    {
      heading: 'For You',
      items: [
        { label: 'For Individuals', path: '/for-individuals' },
        { label: 'For Teams', path: '/for-teams' },
        { label: 'For Enterprises', path: '/for-enterprises' },
        { label: 'For Institutions', path: '/for-institutions' },
      ],
    },
    {
      heading: 'Platform',
      items: [
        { label: 'Nesolearn Kids', path: '/nesolearn-kids' },
        { label: 'Earn on Nesolearn', path: '/earn' },
        { label: 'Visioneer', path: '/visioneer' },
      ],
    },
  ];

  const refreshOrgProfile = async (orgId) => {
    const { organization } = await getOrganization(orgId);
    setOrgProfile(organization ? { orgId, ...organization } : null);
  };

  const refreshUserProfile = async (uid) => {
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      setUserProfile(snap.exists() ? snap.data() : null);
    } catch (err) {
      console.error('[NavigationBar] Error loading user profile:', err);
    }
  };

  useEffect(() => {
    const unsubscribe = authHelpers.onAuthStateChanged(async (firebaseUser) => {
      if (!firebaseUser) {
        setIsAuthenticated(false);
        setUser(null);
        setOrgProfile(null);
        setUserProfile(null);
        return;
      }

      setIsAuthenticated(true);
      setUser(firebaseUser);

      await refreshUserProfile(firebaseUser.uid);

      const { orgId } = await getUserOrgId(firebaseUser.uid);
      if (orgId) {
        await refreshOrgProfile(orgId);
      } else {
        setOrgProfile(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleProfileUpdated = (e) => {
      const orgId = e?.detail?.orgId;
      if (orgId) refreshOrgProfile(orgId);
    };
    window.addEventListener("org-profile-updated", handleProfileUpdated);
    return () => window.removeEventListener("org-profile-updated", handleProfileUpdated);
  }, []);

  // Updated instantly by EditProfileModal after a successful save — no
  // refetch needed since the new values are passed in the event detail.
  useEffect(() => {
    const handleUserProfileUpdated = (e) => {
      const { name, email, avatar } = e?.detail || {};
      setUserProfile((prev) => ({
        ...prev,
        ...(name !== undefined ? { name } : {}),
        ...(email !== undefined ? { email } : {}),
        ...(avatar !== undefined ? { avatar } : {}),
      }));
    };
    window.addEventListener('user-profile-updated', handleUserProfileUpdated);
    return () => window.removeEventListener('user-profile-updated', handleUserProfileUpdated);
  }, []);

  useEffect(() => {
    setIsVideoMode(location.pathname === '/video-learning-interface');
  }, [location.pathname]);

  // Nav starts blended into the hero (no pill) on load, and becomes the
  // floating pill once the user scrolls — same pattern as Jitter's navbar.
  useEffect(() => {
    const SCROLL_THRESHOLD = 24;
    const handleScroll = () => setIsScrolled(window.scrollY > SCROLL_THRESHOLD);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isVideoMode) {
      let hideTimer;
      let showTimer;

      const handleMouseMove = () => {
        setIsNavVisible(true);
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => {
          setIsNavVisible(false);
        }, 3000);
      };

      const handleMouseLeave = () => {
        clearTimeout(showTimer);
        showTimer = setTimeout(() => {
          setIsNavVisible(false);
        }, 1000);
      };

      const handleMouseEnter = () => {
        clearTimeout(showTimer);
        setIsNavVisible(true);
      };

      if (isVideoMode) {
        document.addEventListener('mousemove', handleMouseMove);
        const navElement = document.querySelector('[data-nav="main"]');
        if (navElement) {
          navElement?.addEventListener('mouseenter', handleMouseEnter);
          navElement?.addEventListener('mouseleave', handleMouseLeave);
        }
      }

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        const navElement = document.querySelector('[data-nav="main"]');
        if (navElement) {
          navElement?.removeEventListener('mouseenter', handleMouseEnter);
          navElement?.removeEventListener('mouseleave', handleMouseLeave);
        }
        clearTimeout(hideTimer);
        clearTimeout(showTimer);
      };
    } else {
      setIsNavVisible(true);
    }
  }, [isVideoMode]);

  const accountMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setActiveDropdown((prev) => (prev === 'Account' ? null : prev));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await authHelpers.signOut();
    setIsMobileMenuOpen(false);
    navigate('/authentication-portal');
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const toggleDropdown = (label) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    closeMobileMenu();
    navigate(`/search?q=${encodeURIComponent(q)}`);
    
  };
    const displayName = userProfile?.name || user?.displayName || user?.email?.split('@')?.[0] || 'User';
  const displayAvatar = userProfile?.avatar || user?.photoURL || null;

  // Floating pill instead of a full-width bar — same fixed/z-index/hide-on-
  // video-mode behavior as before, just centered with a combined
  // translateX/translateY transform since Tailwind's translate-x-1/2 and a
  // conditional translate-y class would otherwise fight over the transform.
  const navTransform = `translateX(-50%) ${isVideoMode && !isNavVisible ? 'translateY(-150%)' : 'translateY(0)'}`;

  return (
    <>
                        <nav
        className="fixed top-4 left-1/2 z-nav transition-transform duration-300 ease-in-out"
        style={{ transform: navTransform }}
        data-nav="main"
      >
                                                                         <div
          className={`flex w-[min(94vw,72rem)] items-center gap-3 rounded-full px-4 py-2 sm:gap-4 sm:px-6 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-[1.02] ${
            !themed && !isScrolled ? 'border border-transparent shadow-none' : 'border shadow-lg backdrop-blur-sm'
          }`}
          style={themed ? undefined : (!isScrolled ? FIXED_LIGHT.pillTransparent : FIXED_LIGHT.pill)}
        >
          {/* Logo */}
          <Link
            to="/home-landing-page"
            className="flex items-center space-x-2 transition-smooth hover:opacity-80 flex-shrink-0"
                        onClick={closeMobileMenu}
          >
                        <img
              src="https://pub-b365ff8e20e448c3a7257b35173d9a38.r2.dev/NESOL_2D_Logo_-_Black_and_White-removebg-preview.png"
              alt="Nesolearn Logo"
              className={`w-8 h-8 object-contain ${themed ? 'dark:invert' : ''} ${light ? 'brightness-0 invert' : ''}`}
            />
                                                <span className={`text-lg font-bold hidden sm:block ${themed ? 'text-foreground' : ''}`} style={themed ? undefined : FIXED_LIGHT.wordmark}>
              Nesolearn
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2 flex-1">
            {/* Explore Mega Menu */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('Explore')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
                            <button
                                className={`
                  flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm font-medium
                  transition-smooth min-h-touch transform transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-110
                  ${location.pathname === '/course-catalog'
                    ? 'bg-accent text-accent-foreground'
                    : `${themed ? 'text-foreground' : ''} ${light ? 'hover:bg-white/20' : 'hover:text-foreground hover:bg-muted'}`
                  }
                `}
                style={themed || location.pathname === '/course-catalog' ? undefined : FIXED_LIGHT.exploreBtn}
              >
                <span>Explore</span>
                <Icon name="ChevronDown" size={14} className={`transition-transform ${activeDropdown === 'Explore' ? 'rotate-180' : ''}`} />
              </button>

              {activeDropdown === 'Explore' && (
                <div className="absolute top-full left-0 mt-3 w-[640px] bg-card border border-border rounded-xl shadow-xl p-6 z-dropdown grid grid-cols-3 gap-6">
                  {exploreSections.map((section) => (
                    <div key={section.heading}>
                      <p className="px-2 pb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {section.heading}
                      </p>
                      <div className="space-y-0.5">
                        {section.items.map((dropItem) => (
                          <Link
                            key={dropItem.path}
                            to={dropItem.path}
                            className="block px-2 py-2 rounded-md text-sm text-foreground hover:bg-muted transition-smooth"
                            onClick={() => setActiveDropdown(null)}
                          >
                            {dropItem.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Search Bar */}
                        <form onSubmit={handleSearch} className="relative flex-1 max-w-xs">
              <input
                type="text"
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-8 pl-9 pr-3 text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
                style={themed ? undefined : FIXED_LIGHT.searchWrap}
              />
              <Icon
                name="Search"
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                style={themed ? undefined : FIXED_LIGHT.searchIcon}
              />
            </form>
          </div>

          {/* Desktop Auth Section */}
          <div className="hidden lg:flex items-center flex-shrink-0">
            {isAuthenticated ? (
              <div className="relative" ref={accountMenuRef}>
                                <button
                  onClick={() => setActiveDropdown(activeDropdown === 'Account' ? null : 'Account')}
                  className="flex items-center space-x-2 px-1 py-1 rounded-full hover:bg-muted transition-smooth transform transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-110"
                >
                  {orgProfile?.logoUrl ? (
                    <img
                      src={orgProfile.logoUrl}
                      alt={orgProfile.name}
                      className="w-8 h-8 rounded-full object-cover border border-border"
                    />
                  ) : displayAvatar ? (
                    <img
                      src={displayAvatar}
                      alt={displayName}
                      className="w-8 h-8 rounded-full object-cover border border-border"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} color="white" />
                    </div>
                  )}
                  <Icon name="ChevronDown" size={14} className={`text-muted-foreground transition-transform ${activeDropdown === 'Account' ? 'rotate-180' : ''}`} />
                </button>

                {activeDropdown === 'Account' && (
                  <div className="absolute top-full right-0 mt-3 w-64 bg-card border border-border rounded-xl shadow-xl py-2 z-dropdown">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-medium text-foreground truncate">
                        {orgProfile?.name || displayName}
                      </p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{userProfile?.email || user?.email}</p>
                    </div>
                    <Link
                      to={orgProfile ? "/organization-dashboard" : "/user-dashboard"}
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-foreground hover:bg-muted transition-smooth"
                      onClick={() => setActiveDropdown(null)}
                    >
                      <Icon name="LayoutDashboard" size={16} />
                      <span>Dashboard</span>
                    </Link>

                    <a href="mailto:info@nesolearn.com"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-foreground hover:bg-muted transition-smooth">
                      <Icon name="Mail" size={16} />
                      <span>Contact Support</span>
                    </a>
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-error hover:bg-muted transition-smooth"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                                <Link to="/authentication-portal">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`rounded-full ${light ? '!text-white hover:!bg-white/20' : 'hover:!bg-black hover:!text-white'} transform transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-110`}
                  >
                    Log In
                  </Button>
                </Link>
                                                               <Link to="/authentication-portal">
                  <Button
                    variant="default"
                    size="sm"
                    className="rounded-full !bg-black !text-white hover:!bg-black/90 transform transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-110"
                    style={{ backgroundColor: '#000000', color: '#ffffff' }}
                  >
                    Join for Free
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden ml-auto rounded-full"
            onClick={handleMobileMenuToggle}
            iconName={isMobileMenuOpen ? "X" : "Menu"}
            iconSize={20}
          />
        </div>
      </nav>

            {/* Mobile Menu Drawer */}
            <div
        className={`
          fixed inset-0 z-drawer lg:hidden transition-opacity duration-300
          ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={closeMobileMenu}
        />

        {/* Drawer */}
                <div
          className={`
            absolute top-0 left-0 h-full w-80 max-w-[85vw] border-r ${themed ? 'bg-card text-foreground border-border' : ''}
            transform transition-transform duration-300 ease-in-out overflow-y-auto
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
          style={themed ? undefined : { ...FIXED_LIGHT.drawer, borderColor: '#e5e7eb' }}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <Link
                to="/home-landing-page"
                className="flex items-center space-x-2"
                onClick={closeMobileMenu}
              >
                                <img
                  src="https://pub-b365ff8e20e448c3a7257b35173d9a38.r2.dev/NESOL_2D_Logo_-_Black_and_White-removebg-preview.png"
                  alt="Nesolearn Logo"
                  className={`w-8 h-8 object-contain ${themed ? 'dark:invert' : ''}`}
                />
                                                                <span className={`text-xl font-bold ${themed ? 'text-foreground' : ''}`} style={themed ? undefined : FIXED_LIGHT.wordmark}>
                  Nesolearn
                </span>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeMobileMenu}
                iconName="X"
                iconSize={20}
              />
            </div>

            {/* Mobile Search Bar */}
            <div className="p-4 border-b border-border">
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 text-sm bg-muted border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
                />
                <Icon
                  name="Search"
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                />
              </form>
            </div>

            {/* Explore — accordion with grouped sections */}
            <div className="flex-1 py-2">
              <button
                onClick={() => toggleDropdown('Explore')}
                className="flex items-center justify-between w-full px-4 py-3 text-base font-medium text-foreground hover:bg-muted transition-smooth min-h-touch"
              >
                <span>Explore</span>
                <Icon
                  name="ChevronDown"
                  size={16}
                  className={`transition-transform ${activeDropdown === 'Explore' ? 'rotate-180' : ''}`}
                />
              </button>

              {activeDropdown === 'Explore' && (
                <div className="bg-muted/30 py-2">
                  {exploreSections.map((section) => (
                    <div key={section.heading} className="mb-2 last:mb-0">
                      <p className="px-8 py-1 text-xs font-semibold text-muted-foreground uppercase">
                        {section.heading}
                      </p>
                      {section.items.map((dropItem) => (
                        <Link
                          key={dropItem.path}
                          to={dropItem.path}
                          onClick={closeMobileMenu}
                          className="block px-8 py-2 text-sm text-foreground hover:bg-muted transition-smooth"
                        >
                          {dropItem.label}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Auth Section */}
            <div className="border-t border-border p-4">
              {isAuthenticated && (
                <p className="px-1 pb-2 text-xs font-semibold text-muted-foreground uppercase">Account</p>
              )}
              {isAuthenticated ? (
                <div className="space-y-3">
                  <Link to={orgProfile ? "/organization-dashboard" : "/user-dashboard"} onClick={closeMobileMenu}>
                    <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg hover:bg-muted/80 transition-smooth">
                      {orgProfile?.logoUrl ? (
                        <img
                          src={orgProfile.logoUrl}
                          alt={orgProfile.name}
                          className="w-10 h-10 rounded-full object-cover border border-border"
                        />
                      ) : displayAvatar ? (
                        <img
                          src={displayAvatar}
                          alt={displayName}
                          className="w-10 h-10 rounded-full object-cover border border-border"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                          <Icon name="User" size={20} color="white" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-foreground">
                          {orgProfile?.name || displayName}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {userProfile?.email || user?.email || 'user@example.com'}
                        </p>
                      </div>
                    </div>
                  </Link>
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={handleLogout}
                    iconName="LogOut"
                    iconPosition="left"
                  >
                    Logout
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Link to="/authentication-portal" onClick={closeMobileMenu}>
                    <Button variant="outline" fullWidth>
                      Log In
                    </Button>
                  </Link>
                  <Link to="/authentication-portal" onClick={closeMobileMenu}>
                    <Button variant="default" fullWidth>
                      Join Now
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      </>
  );
};

export default NavigationBar;